'use client';

import { cn } from '@/lib/utils';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerVariant = 'default' | 'dots' | 'pulse';

interface SpinnerProps {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  className?: string;
}

const sizes: Record<SpinnerSize, number> = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
};

/**
 * Default spinner - rotating arc
 */
function DefaultSpinner({ size = 'md', className }: Omit<SpinnerProps, 'variant'>) {
  const pixelSize = sizes[size];
  const strokeWidth = size === 'xs' ? 2 : size === 'sm' ? 3 : 4;

  return (
    <svg
      width={pixelSize}
      height={pixelSize}
      viewBox="0 0 24 24"
      fill="none"
      className={cn('text-accent-primary', className)}
      style={{
        animation: 'spin 1s linear infinite',
      }}
      role="status"
      aria-label="Loading"
    >
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Background circle */}
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        opacity="0.2"
        fill="none"
      />
      {/* Animated arc */}
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeDasharray="32 32"
      />
    </svg>
  );
}

/**
 * Dots spinner - three bouncing dots
 */
function DotsSpinner({ size = 'md', className }: Omit<SpinnerProps, 'variant'>) {
  const dotSize = size === 'xs' ? 4 : size === 'sm' ? 6 : size === 'md' ? 8 : size === 'lg' ? 12 : 16;
  const gap = size === 'xs' ? 2 : size === 'sm' ? 3 : 4;

  return (
    <div
      className={cn('flex items-center', className)}
      style={{ gap }}
      role="status"
      aria-label="Loading"
    >
      <style>
        {`
          @keyframes bounce-dot {
            0%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-50%); }
          }
        `}
      </style>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="bg-accent-primary rounded-full"
          style={{
            width: dotSize,
            height: dotSize,
            animation: 'bounce-dot 0.6s ease-in-out infinite',
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Pulse spinner - pulsing dot
 */
function PulseSpinner({ size = 'md', className }: Omit<SpinnerProps, 'variant'>) {
  const pixelSize = sizes[size];

  return (
    <div
      className={cn('relative', className)}
      style={{ width: pixelSize, height: pixelSize }}
      role="status"
      aria-label="Loading"
    >
      <style>
        {`
          @keyframes ping-slow {
            0% { transform: scale(1); opacity: 0.8; }
            75%, 100% { transform: scale(2); opacity: 0; }
          }
        `}
      </style>
      {/* Pulsing ring */}
      <div
        className="absolute inset-0 rounded-full bg-accent-primary"
        style={{
          animation: 'ping-slow 1.5s cubic-bezier(0, 0, 0.2, 1) infinite',
        }}
      />
      {/* Center dot */}
      <div
        className="absolute rounded-full bg-accent-primary"
        style={{
          top: '25%',
          left: '25%',
          width: '50%',
          height: '50%',
        }}
      />
    </div>
  );
}

/**
 * Reusable Spinner Component
 *
 * @example
 * <Spinner />
 * <Spinner size="lg" />
 * <Spinner variant="dots" />
 */
export function Spinner({ size = 'md', variant = 'default', className }: SpinnerProps) {
  switch (variant) {
    case 'dots':
      return <DotsSpinner size={size} className={className} />;
    case 'pulse':
      return <PulseSpinner size={size} className={className} />;
    default:
      return <DefaultSpinner size={size} className={className} />;
  }
}
